import { useQuery, useReactiveVar } from '@apollo/client';
import { parseStartParamAnalytics, resolvePremiumStatus, type PremiumStatusId } from '@focuslee/shared';
import { initData, retrieveLaunchParams, useSignal } from '@tma.js/sdk-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loginData } from '../client/apollo';
import { GetProfileQuery, GetProfileQueryVariables } from '../client/generated/graphql';
import { getProfileQuery } from '../client/queries/profile';

type UserIdentifyTrackerProps = {
    identifyUser: (props: {
        locale?: string;
        premiumStatus?: PremiumStatusId;
        cohort?: string;
        inviteCode?: string;
        referrerUserId?: string;
    }) => void;
};

export function UserIdentifyTracker({ identifyUser }: UserIdentifyTrackerProps) {
    const { i18n } = useTranslation();
    const authData = useReactiveVar(loginData);
    const tgUser = useSignal(initData.user);
    const { data } = useQuery<GetProfileQuery, GetProfileQueryVariables>(getProfileQuery, {
        skip: !authData?.value,
        fetchPolicy: 'cache-first',
    });
    const lastKey = useRef<string>('');

    useEffect(() => {
        const role = data?.user?.role ?? authData?.role ?? null;
        const tariff = data?.user?.tariff ?? null;
        const premiumStatus = resolvePremiumStatus(role, tariff);
        const startParam =
            retrieveLaunchParams().tgWebAppStartParam ?? new URLSearchParams(window.location.search).get('startapp');
        const parsed = parseStartParamAnalytics(startParam);
        const locale = i18n.language || tgUser?.language_code || undefined;
        const key = JSON.stringify({ locale, premiumStatus, parsed });
        if (key === lastKey.current) return;
        lastKey.current = key;
        identifyUser({
            locale,
            premiumStatus,
            cohort: parsed.cohort,
            inviteCode: parsed.invite_code,
            referrerUserId: parsed.referrer_user_id,
        });
    }, [authData?.role, data?.user?.role, data?.user?.tariff, i18n.language, identifyUser, tgUser?.language_code]);

    return null;
}
